import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import { LogOut, Book, Pencil, Award, X, Trophy } from 'lucide-react';

// Use relative URLs in production, full URL only in preview
const API = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png";

// Logo component
const WillowLogo = ({ size = "h-10" }) => (
  <img src={LOGO_URL} alt="Willow Notes" className={`${size} object-contain`} />
);

// ==================== STICKER SVG ART ====================

const FlowerSticker = ({ variant = 0 }) => {
  const imgs = ['/kawaii/flower1.png', '/kawaii/flower2.png', '/kawaii/flower3.png', '/kawaii/flower4.png'];
  return <img src={imgs[variant % imgs.length]} alt="Flower" className="w-full h-full object-contain" draggable={false} />;
};

const AnimalSticker = () => (
  <img src="/kawaii/bunny.png" alt="Bunny" className="w-full h-full object-contain" draggable={false} />
);

// Rainbow Parrot — kawaii
const ParrotSticker = () => (
  <img src="/kawaii/parrot_sticker.png" alt="Parrot" className="w-full h-full object-contain" draggable={false} />
);

// Busy Bee (10 drawings) — kawaii
const BeeSticker = () => (
  <img src="/kawaii/bee.png" alt="Bee" className="w-full h-full object-contain" draggable={false} />
);

// Exploring Fox (landscape) — kawaii
const FoxSticker = () => (
  <img src="/kawaii/fox.png" alt="Fox" className="w-full h-full object-contain" draggable={false} />
);

// Posing Cat (portrait) — kawaii
const CatSticker = () => (
  <img src="/kawaii/cat.png" alt="Cat" className="w-full h-full object-contain" draggable={false} />
);

// Crafty Owl (3 in a day) — kawaii
const OwlSticker = () => (
  <img src="/kawaii/owl_sticker.png" alt="Owl" className="w-full h-full object-contain" draggable={false} />
);

const VintageSticker = ({ variant = 0 }) => {
  const imgs = ['/kawaii/vintage1.png', '/kawaii/vintage2.png', '/kawaii/vintage3.png'];
  return <img src={imgs[variant % imgs.length]} alt="Vintage" className="w-full h-full object-contain" draggable={false} />;
};

const SpecialSticker = ({ variant = 0 }) => {
  const imgs = [
    '/kawaii/award_star.png',
    '/kawaii/award_pencil.png',
    '/kawaii/award_diary.png',
    '/kawaii/award_mood.png',
    '/kawaii/award_calendar.png',
    '/kawaii/award_heart.png',
    '/kawaii/award_crown.png',
    '/kawaii/award_books.png',
    '/kawaii/award_quill.png',
    '/kawaii/award_trophy.png',
  ];
  return <img src={imgs[variant % imgs.length]} alt="Award" className="w-full h-full object-contain" draggable={false} />;
};

// Sticker ID → SVG component mapping
const STICKER_VISUALS = {
  'flower-3':  <FlowerSticker variant={0} />,
  'flower-5':  <FlowerSticker variant={1} />,
  'flower-10': <FlowerSticker variant={2} />,
  'flower-15': <FlowerSticker variant={3} />,
  'animal-weekly': <AnimalSticker />,
  'animal-rainbow': <ParrotSticker />,
  'animal-10drawings': <BeeSticker />,
  'animal-landscape': <FoxSticker />,
  'animal-portrait': <CatSticker />,
  'animal-3inday': <OwlSticker />,
  'vintage-3':  <VintageSticker variant={0} />,
  'vintage-5':  <VintageSticker variant={1} />,
  'vintage-10': <VintageSticker variant={2} />,
  'special-welcome':       <SpecialSticker variant={0} />,
  'special-first-drawing':  <SpecialSticker variant={1} />,
  'special-first-entry':    <SpecialSticker variant={2} />,
  'special-first-mood':     <SpecialSticker variant={3} />,
  'special-1month':         <SpecialSticker variant={4} />,
  'special-6months':        <SpecialSticker variant={5} />,
  'special-1year':          <SpecialSticker variant={6} />,
  'special-10entries':      <SpecialSticker variant={7} />,
  'special-25entries':      <SpecialSticker variant={8} />,
  'special-50entries':      <SpecialSticker variant={9} />,
};

// Category labels and order
const CATEGORIES = [
  { key: 'special', label: 'Special', color: '#FFD6E0' },
  { key: 'flowers', label: 'Streaks', color: '#C8F4D6' },
  { key: 'animals', label: 'Drawing', color: '#E0D4FF' },
  { key: 'vintage', label: 'Calendar', color: '#FFD6E0' },
];

export default function Stickers() {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stickers, setStickers] = useState({});
  const [definitions, setDefinitions] = useState({});
  const [celebration, setCelebration] = useState(null);
  const [selectedSticker, setSelectedSticker] = useState(null);

  const fetchStickers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stickers`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStickers(data.stickers);
        setDefinitions(data.definitions);
      }
    } catch (err) {
      console.error('Failed to fetch stickers:', err);
    }
  }, []);

  const checkNewStickers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stickers/check`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.newly_earned.length > 0) {
          // Show celebration for the first newly earned sticker
          setCelebration(data.newly_earned[0]);
          setTimeout(() => setCelebration(null), 3000);
          // Re-fetch stickers to update the grid
          fetchStickers();
        }
      }
    } catch (err) {
      console.error('Failed to check stickers:', err);
    }
  }, [fetchStickers]);

  useEffect(() => {
    fetchStickers().then(() => checkNewStickers());
  }, [fetchStickers, checkNewStickers]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    logout();
    navigate('/login');
  };

  const formatEarnedDate = (isoStr) => {
    const d = new Date(isoStr);
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} at ${time}`;
  };

  // Group stickers by category
  const grouped = {};
  for (const cat of CATEGORIES) {
    grouped[cat.key] = [];
  }
  for (const [sid, def] of Object.entries(definitions)) {
    if (grouped[def.category]) {
      grouped[def.category].push({ id: sid, ...def });
    }
  }

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <SettingsGear />
      {/* Celebration overlay */}
      {celebration && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm" data-testid="sticker-celebration">
          <div className="flex flex-col items-center gap-4">
            <div className="w-32 h-32 animate-spin-slow">
              {STICKER_VISUALS[celebration]}
            </div>
            <p className="text-2xl font-heading font-bold text-[#1A1A1A] bg-white/90 px-6 py-3 rounded-full shadow-lg">
              {t('newAwardEarned')}
            </p>
            <p className="text-lg font-medium text-white bg-[#1A1A1A]/60 px-4 py-1 rounded-full">
              {definitions[celebration]?.name}
            </p>
          </div>
        </div>
      )}

      {/* Selected sticker modal */}
      {selectedSticker && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/20 backdrop-blur-sm" onClick={() => setSelectedSticker(null)}>
          <div className="bg-white rounded-3xl border-[1.5px] border-[var(--border-soft)] shadow-xl p-8 max-w-xs text-center" onClick={e => e.stopPropagation()} data-testid="sticker-detail-modal">
            <button onClick={() => setSelectedSticker(null)} className="absolute top-3 right-3 text-[#9CA3AF] hover:text-[#1A1A1A]">
              <X className="w-5 h-5" />
            </button>
            <div className="w-24 h-24 mx-auto mb-4">
              {STICKER_VISUALS[selectedSticker.id]}
            </div>
            <h3 className="text-2xl font-heading font-bold text-[#1A1A1A] mb-1">{selectedSticker.name}</h3>
            <p className="text-sm text-[#6B7280] mb-3">{selectedSticker.desc}</p>
            {stickers[selectedSticker.id] ? (
              <div className="bg-[var(--c2-40)] rounded-full px-4 py-2 text-sm font-medium text-[#1A1A1A]" data-testid="sticker-earned-date">
                {t('earnedLabel')}: {formatEarnedDate(stickers[selectedSticker.id])}
              </div>
            ) : (
              <div className="bg-[#F3F4F6] rounded-full px-4 py-2 text-sm font-medium text-[#9CA3AF]">
                {t('notYetEarned')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b-[1.5px] border-[var(--border-soft)] px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WillowLogo size="h-24" />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-diary"
            >
              <Book className="w-4 h-4 inline mr-1" /> {t('diary')}
            </button>
            <button
              onClick={() => navigate('/drawings')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c3)] transition-colors"
              data-testid="nav-drawings"
            >
              <Pencil className="w-4 h-4 inline mr-1" /> {t('drawings')}
            </button>
            <button
              className="px-5 py-2 rounded-full bg-[var(--c2)] border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A]"
              data-testid="nav-stickers"
            >
              <Award className="w-4 h-4 inline mr-1" /> {t('awards')}
            </button>
            <button
              onClick={() => navigate('/leaderboard')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-leaderboard"
            >
              <Trophy className="w-4 h-4 inline mr-1" /> Leaderboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            {user?.avatar && (
              <img src={`/avatars/${user.avatar}.png`} alt="avatar" className="w-8 h-8 rounded-full border-[1.5px] border-[var(--border-soft)] object-cover" data-testid="navbar-avatar" />
            )}
            <span className="text-[#1A1A1A] font-semibold hidden sm:inline">{t('hi')}, {user?.name}!</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c1)] text-[#1A1A1A] font-heading font-bold text-base transition-all"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 inline mr-1" /> {t('logout')}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-heading font-bold text-[#1A1A1A]" data-testid="stickers-heading">{t('awardsHeading')}</h2>
          <p className="text-[#6B7280] mt-1 font-medium">
            {Object.keys(stickers).length} / {Object.keys(definitions).length} {t('collected')}
          </p>
        </div>

        {CATEGORIES.map(cat => (
          <div key={cat.key} className="mb-10" data-testid={`sticker-category-${cat.key}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
              <h3 className="text-2xl font-heading font-bold text-[#1A1A1A]">{cat.label}</h3>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {grouped[cat.key]?.map(s => {
                const earned = !!stickers[s.id];
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSticker(s)}
                    className={`group relative aspect-square rounded-3xl border-[1.5px] p-3 transition-all ${
                      earned
                        ? 'bg-white border-[var(--border-soft)] shadow-sm hover:shadow-md hover:-translate-y-0.5'
                        : 'bg-[#F9FAFB] border-[#E5E7EB] opacity-40 grayscale hover:opacity-60'
                    }`}
                    data-testid={`sticker-${s.id}`}
                  >
                    <div className="w-full h-full">
                      {STICKER_VISUALS[s.id]}
                    </div>
                    <p className={`absolute bottom-1 left-0 right-0 text-center text-xs font-bold truncate px-1 ${
                      earned ? 'text-[#1A1A1A]' : 'text-[#9CA3AF]'
                    }`}>
                      {s.name}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
